// @flow
import { _ } from "extensions/underscore";
import { captureBreadcrumb } from "extensions/sentry";

import { QualifiersParsingError, QualifiersNotAllowedCategoryError } from "common/helpers/errors";

type QualifierOperatorType = "=" | "!="; // Only two operators for now

type QualifierValueType = {
    operator: QualifierOperatorType,
    value: string
};

type QualifierCategoryType = {
    category: string,
    values: Array<QualifierValueType>
};

export type TokenType = {
    value: string,
    label: string,
    operator: QualifierOperatorType,
    applied: boolean,
    category: string,
    ref: string
};

type QualifiersInstanceOptionsType = {
    value: string,
    whitelisted?: Array<string>
};

type OptionsType = {
    silent: boolean
};

type RemoveOptionsType = {
    value: boolean
};

const _decodeURIComponent = function(input) {
    try {
        return decodeURIComponent(input);
    } catch (error) {
        captureBreadcrumb({
            message: "Cannot decode string qualifier value",
            data: {
                input,
                error: error.name,
                message: error.message
            }
        });
        
        return input;
    }
};

export class Qualifiers {
    constructor({ value, whitelisted = [] }: QualifiersInstanceOptionsType = {}) {
        this.whitelisted = whitelisted;

        this.initialize(value);
    }

    value: Array<QualifierCategoryType>;
    
    whitelisted: Array<string>;

    initialize(value: string | void) {
        if (typeof value === "string") {
            // Check if input is encoded
            const isEncoded = _decodeURIComponent(value) !== value;

            // And decode in parser if it is encoded
            const parsed = Qualifiers.parser(value, isEncoded);

            // Now check if we should check against provided "whitelisted" categories
            if (this.whitelisted.length) {
                parsed.forEach((q: QualifierCategoryType) => {
                    if (!this.whitelisted.includes(q.category)) {
                        throw new QualifiersNotAllowedCategoryError({
                            message: "Category is not allowed",
                            extra: {
                                string: value,
                                category: q.category
                            },
                            tags: {
                                silent: 1
                            }
                        });
                    }
                });
            }

            this.value = parsed;
        } else {
            this.value = [];
        }
    }

    toString(encode = false): string {
        return Qualifiers.serializer(this.value, encode);
    }

    /**
     * Get category values
     * @param category
     * @param includeOperator
     * @returns {*}
     */
    get(category: string, includeOperator = false): Array<string | QualifierValueType> {
        if (category) {
            const match = this.value.find((q: QualifierCategoryType): boolean => q.category === category);
            if (match && match.values) {
                return match.values.map((v: QualifierValueType): string | QualifierValueType => {
                    if (includeOperator) {
                        return v;
                    }

                    return v.value;
                });
            }
        }

        return [];
    }

    /**
     * Check if qualifiers object has provided category qualifier
     * @param category
     */
    has(category: string, operator: ?QualifierOperatorType): boolean {
        if (category) {
            return this.value.some((q: QualifierCategoryType): boolean => {
                const matchCategory = q.category === category;
                let matchOperator = true;
                if (typeof operator === "string") {
                    matchOperator = q.values.some((v) => v.operator === operator);
                }

                return matchCategory && matchOperator;
            });
        }

        return false;
    }

    /**
     * Get full qualifiers list
     * @returns {*}
     */
    toJSON(): Array<QualifierCategoryType> {
        return _.clone(this.value);
    }

    add(qualifier: string) {
        const str = `${this.toString()} ${qualifier}`;
        this.initialize(str);
    }
    
    merge(subset: string | Qualifiers) {
        // Normalize subset to be a qualifier
        if (typeof subset === "string") {
            subset = new Qualifiers({
                value: subset
            });
        }
        
        if (subset.size() > 0) {
            subset.value.forEach(({ category, values }) => {
                const subsetPlus = values.filter(({ operator }) => operator === "=").map(({ value }) => Qualifiers.normalizeValue(value));
                const subsetMinus = values.filter(({ operator }) => operator === "!=").map(({ value }) => Qualifiers.normalizeValue(value));
                
                if (this.has(category)) {
                    const supersetValues = this.get(category, { includeOperator: true });
    
                    const supersetPlus = supersetValues.filter(({ operator }) => operator === "=").map(({ value }) => value);
                    // eslint-disable-next-line
                    const supersetMinus = supersetValues.filter(({ operator }) => operator === "!=").map(({ value }) => value);
                    
                    subsetPlus.forEach((subsetValue) => {
                        // if the superset has method:MC and the subset has method:VI that should equal 0.
                        if (!supersetPlus.includes(subsetValue)) {
                            this.add(`${category}:${subsetValue}`);
                            if (supersetPlus.length && !supersetMinus.includes(subsetValue)) {
                                this.add(`-${category}:${subsetValue}`);
                            }
                        }
                    });
    
                    subsetMinus.forEach((subsetValue) => {
                        if (!supersetPlus.includes(subsetValue)) {
                            this.add(`-${category}:${subsetValue}`);
                        }
                    });
                    
                    supersetPlus.forEach((superSetValue) => {
                        if (!subsetPlus.includes(superSetValue)) {
                            this.value = this.value.map((v: QualifierCategoryType): QualifierCategoryType => {
                                if (v.category === category) {
                                    v.values = v.values.filter((vv: QualifierValueType): boolean => vv.value !== superSetValue);
                                }
                                return v;
                            });
                        }
                    });
                } else {
                    subsetPlus.forEach((value) => this.add(`${category}:${value}`));
                    subsetMinus.forEach((value) => this.add(`-${category}:${value}`));
                }
            });
        }
    }

    /**
     * Replace single qualifier with new value
     * @param qualifier
     */
    replace(qualifier: string, { silent = false }: OptionsType = {}) {
        const [parsed = false] = Qualifiers.parser(qualifier);

        if (parsed) {
            const current = this.value.find((q: QualifierCategoryType): boolean => q.category === parsed.category);
            if (current) {
                current.values = parsed.values;
            }
        }
    }
    
    addOrReplace(qualifiers: string) {
        const [parsed = false] = Qualifiers.parser(qualifiers);
        if (parsed) {
            // Check if we have already a qualifier of the same category
            if (this.get(parsed.category).length) {
                this.replace(qualifiers);
            } else {
                this.add(qualifiers);
            }
        }
    }

    /**
     * Remove all qualifiers with provided category from the value
     * @param category
     */
    remove(category: string, { value = false }: RemoveOptionsType = {}) {
        if (value) {
            this.value = this.value.map((v: QualifierCategoryType): QualifierCategoryType => {
                if (v.category === category) {
                    v.values = v.values.filter((vv: QualifierValueType): boolean => vv.value !== value);
                }
                return v;
            });
        } else {
            this.value = this.value.filter((v: QualifierCategoryType): boolean => v.category !== category);
        }
    }

    /**
     * Reset qualifiers to an empty array
     */
    clean() {
        this.initialize();
    }

    /**
     * Returns a new instance of the qualifiers with an identical value.
     */
    clone(): Qualifiers {
        return new Qualifiers({
            value: this.toString()
        });
    }

    /**
     * Returns an object with key:[values] props ignoring operator type
     */
    toObject(): { [string]: Array<string> } {
        return this.value.reduce((memo, curr: QualifierCategoryType) => ({
            ...memo,
            [curr.category]: curr.values.map((v) => v.value)
        }), {});
    }

    /**
     * Returns an object consumable by tokenfield component
     */
    toTokens(): Array<TokenType> {
        return this.toJSON().reduce((memo: Array<TokenType>, curr: QualifierCategoryType): Array<TokenType> => {
            const mappedArray = curr.values.map((v: QualifierValueType): TokenType => {
                const normalizedValue = Qualifiers.normalizeValue(v.value);

                return {
                    value: `${v.operator === "=" ? "+" : "-"}${curr.category}:${normalizedValue}`,
                    label: v.value, // Add alias here
                    operator: v.operator,
                    category: curr.category,
                    applied: true,
                    ref: v.value // Reference is used to flip operators and values. We can't do it by label because label is transformed to an alias in normalizeToken method in queryBuilder
                };
            });
            memo.push(...mappedArray);

            return memo;
        }, []);
    }

    size(): number {
        return this.value.length;
    }

    static get delimiter(): string {
        return " ";
    }

    // Wrap value in quotes if it contains space, comma, or colon
    static normalizeValue(value = "") {
        if (value === null) {
            value = "null";
        }
        
        if (typeof value === "boolean" || Number.isFinite(value)) { // Handle numerical values and booleans
            value = value.toString();
        }
        
        if (Array.isArray(value)) {
            return value.map((arrayItem) => Qualifiers.normalizeValue(arrayItem)).join(",");
        }

        const val = value.replace(/"/g, "&quot;").replace(/'/g, "&#x27;");
        if ([Qualifiers.delimiter, ":", ",", "\t"].some((c: string): boolean => val.includes(c))) {
            return `"${val}"`;
        }
        return val;
    }

    static parser(qualifiersString: string, decode = false): Array<QualifierCategoryType> {
        if (!_.isString(qualifiersString)) {
            throw new TypeError("Qualifiers string expected");
        }

        let string = qualifiersString.trim();
        if (decode) {
            string = _decodeURIComponent(string);
        }

        if (string === "") {
            return [];
        }

        const parsed = []; // We will push all parsed qualifiers here
        const parsedHash = {}; // Maintain this object for constant lookups
        const terms = []; // Store search terms here

        // Get a list of search terms respecting single and double quotes
        const categoriesRegex = /(?:[^\s"']+|["'][^"']*["'])+/g;
        let match;
        // eslint-disable-next-line no-cond-assign
        while ((match = categoriesRegex.exec(string)) !== null) {
            // $FlowFixMe Suppressing the error because we clearly check for null in the `while` condition
            const term = match[0];
            const sepIndex = term.indexOf(":");
            if (sepIndex !== -1) {
                // Everything before ":" is a category
                let category = term.slice(0, sepIndex);
                if (!category) {
                    throw new Error("Missing category");
                }

                // Get operator
                let operator = "=";
                if (category[0] === "-") {
                    operator = "!=";
                    category = category.substring(1);
                } else if (category[0] === "+") {
                    // Plus is implied
                    category = category.substring(1);
                }

                // Everything after is a value
                const valStr = term.slice(sepIndex + 1);
                if (!valStr) {
                    throw new Error(`Missing value for category: ${term}. Qualifiers string: ${qualifiersString}`);
                }

                // Now let's go over value, assuming we can deal with a mix of quoted and not values, e.g. '"Bank of america, inc",Chase'
                const valuesRegex = /(".*?"|[^\s",]+)(?=\s*,|\s*$)/g;
                const arrValues = [];
                let res;
                // eslint-disable-next-line no-cond-assign
                while ((res = valuesRegex.exec(valStr)) !== null) {
                    arrValues.push({
                        // Stupid Flow throws error here: exec(string: string): RegExp$matchResult | null;
                        // $FlowFixMe Suppressing the error because we clearly check for null in the `while` condition
                        value: res[0].replace(/(?:^")|(?:"$)/g, ""),
                        operator
                    });
                }

                // Push result to temp terms holder
                terms.push({
                    category,
                    values: arrValues
                });
            } else {
                throw new QualifiersParsingError({
                    message: "Wrong token",
                    extra: { term, qualifiersString, decode }
                });
            }
        }

        // For each search term
        terms.forEach((term: QualifierCategoryType) => {
            // Check if we already have keyword
            if (Object.prototype.hasOwnProperty.call(parsedHash, term.category)) {
                const matchingParsedItem = parsed[parsedHash[term.category]];
                matchingParsedItem.values = [...matchingParsedItem.values, ...term.values];
            } else {
                // Push term and store it's position
                parsedHash[term.category] = parsed.push(term) - 1;
            }
        });

        return parsed;
    }

    static serializer(qualifiersObject: Array<QualifierCategoryType>, encode = false): string {
        if (!_.isArray(qualifiersObject)) {
            throw new TypeError("Qualifiers array expected");
        }
        if (!qualifiersObject.length) {
            return "";
        }

        const terms = [];

        // Build terms. We need to separate terms by operators
        qualifiersObject.forEach((q: QualifierCategoryType) => {
            // Group all values by operator they're using
            const groups = _.groupBy(q.values, (v: QualifierValueType): any => v.operator);

            // Now
            _.each(groups, (values: Array<QualifierValueType>, operator: QualifierOperatorType) => {
                const category = q.category;
                // Go over all values and if a value has spaces then wrap it in quotes
                const valuesStr = values.map((v: QualifierValueType): string => Qualifiers.normalizeValue(v.value)).join(",");
                const prefix = operator === "!=" ? "-" : "";

                terms.push(`${prefix}${category}:${valuesStr}`);
            });
        });

        const str = terms.join(Qualifiers.delimiter);
        if (encode) {
            return window.encodeURIComponent(str);
        }

        return str;
    }

    static isValid(qualifiersString: string): boolean {
        let isValid = true;

        try {
            Qualifiers.parser(qualifiersString);
        } catch {
            isValid = false;
        }

        return isValid;
    }
}
